import { ShareableEntity } from "@/lib/api/shareable-entities";
import { EntityHeader } from "../entity-header";
import { NoteView } from "./note-view";
import { EntityComments } from "../interactions/entity-comments";
import { EntityReactions } from "../interactions/entity-reactions";
import { InteractionFooter } from "../interactions/interaction-footer";
import { useEntityInteractionHandlers } from "../interactions/use-entity-interaction-handlers";
import { ScheduledEntityActions } from "../scheduled-entity-actions";
import { DateTime } from "luxon";

interface ViewNoteViewProps {
  entity: ShareableEntity;
  menteeId: string;
  mentorId: string;
  currentIndex?: number;
  totalNotes?: number;
  isLoadingNextPage?: boolean;
  activeTab?: "new" | "all";
  onPrevious?: () => void;
  onNext?: () => void;
  onNavigateToList?: () => void;
}

export function ViewNoteView({
  entity,
  menteeId,
  mentorId,
  currentIndex = 1,
  totalNotes = 1,
  isLoadingNextPage = false,
  activeTab,
  onPrevious,
  onNext,
  onNavigateToList,
}: ViewNoteViewProps) {
  const {
    user,
    isRead,
    isFromCurrentUser,
    comments,
    reactions,
    isInteractionPending,
    handleReaction,
    handleComment,
    toggleReadStatus,
  } = useEntityInteractionHandlers({
    entity,
    menteeId,
    entityType: "note",
    activeTab,
    onNavigateToList,
  });

  const userIsMentor = user?.id === mentorId;
  const isFromOtherUser = !isFromCurrentUser;
  const isScheduled = entity.status === "scheduled";
  const canInteract = !isScheduled && isFromOtherUser && userIsMentor;

  const notePreviewThumbnail = (
    <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-white p-1">
      <div className="h-full w-full overflow-hidden text-[6px] leading-tight text-gray-500">
        {(entity.note?.content ?? "").slice(0, 100)}...
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-white">
      <EntityHeader
        title={`Note from ${isFromOtherUser ? entity.owner.name : "you"}`}
        pagination={{
          current: currentIndex,
          total: totalNotes,
          isLoadingNextPage,
          onPrevious,
          onNext,
        }}
        owner={{
          name: entity.owner.name,
          avatarUrl: entity.owner.profile_picture_url ?? undefined,
        }}
        createdAt={entity.created_at}
        subtitle={
          isScheduled && entity.scheduledAt
            ? `Scheduled for ${DateTime.fromISO(entity.scheduledAt).toFormat("MMM d, h:mm a")}`
            : undefined
        }
        actions={isScheduled ? <ScheduledEntityActions entity={entity} /> : undefined}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <NoteView
          content={entity.note?.content ?? ""}
          createdAt={entity.created_at}
          creatorAvatarUrl={entity.owner.profile_picture_url ?? undefined}
          creatorName={entity.owner.name}
          editMode={false}
        />

        {/* Show reactions if any */}
        {reactions.length > 0 && (
          <EntityReactions reactions={reactions} className="mt-4" />
        )}

        {/* Show comments if any */}
        {comments.length > 0 && (
          <div className="mt-4">
            <EntityComments comments={comments} />
          </div>
        )}
      </div>

      {canInteract && (
        <InteractionFooter
          ownerName={entity.owner.name}
          isRead={isRead}
          isPending={isInteractionPending}
          previewThumbnail={notePreviewThumbnail}
          onReaction={handleReaction}
          onComment={handleComment}
          onMarkAsRead={toggleReadStatus}
        />
      )}
    </div>
  );
}
